import {FirebaseError} from 'firebase/app';
import {User} from 'firebase/auth';
import {onAuthStateChanged} from 'firebase/auth';
import {getAuth} from 'firebase/auth';
import {createUserWithEmailAndPassword} from 'firebase/auth';
import {signInWithEmailAndPassword} from 'firebase/auth';
import {sendPasswordResetEmail} from 'firebase/auth';
import {setPersistence} from 'firebase/auth';
import {reauthenticateWithCredential} from 'firebase/auth';
import {EmailAuthProvider} from 'firebase/auth';
import {browserSessionPersistence} from 'firebase/auth';

// Using `onAuthStateChanged` is the recommended way of getting the currently
// logged-in user (or null if user is not logged in).
// See: https://firebase.google.com/docs/auth/web/manage-users#get_the_currently_signed-in_user
// IMPORTANT: Do not access `auth.currentUser` directly as the value may not be
//            available during Firebase initialization.
export const getCurrentFirebaseUser = async (): Promise<User | null> => {
  return await new Promise<User | null>(resolve => onAuthStateChanged(getAuth(), resolve));
};

export const signUpWithFirebase = async (email: string, password: string) => {
  return await createUserWithEmailAndPassword(getAuth(), email, password);
};

export const signInWithFirebase = async (email: string, password: string, persistAuth: boolean) => {
  const auth = getAuth();

  // If we want to persist auth status across sessions, then we should simply
  // do nothing here, because persisting auth status is the default behaviour
  // of Firebase.

  // If we do not want to persist auth status across sessions ...
  if (!persistAuth) {
    // ... then we instruct Firebase to use a ephemeral persistence option.
    await setPersistence(auth, browserSessionPersistence);
  }

  return await signInWithEmailAndPassword(auth, email, password);
};

export const signOutWithFirebase = async () => {
  return await getAuth().signOut();
};

export const verifyCredentialWithFirebase = async (email: string, password: string) => {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  if (!currentUser) {
    return false;
  }

  try {
    await reauthenticateWithCredential(currentUser, EmailAuthProvider.credential(email, password));
    return true;
  } catch {
    return false;
  }
};

type ResetPasswordWithFirebaseOptions = {
  suppressEmailErrors?: boolean;
};

export const resetPasswordWithFirebase = async (
  email: string,
  opts?: ResetPasswordWithFirebaseOptions,
) => {
  let firebaseOpts: {url: string} | undefined;

  if (process.env.NEXT_PUBLIC_FE_URL) {
    firebaseOpts = {url: `${process.env.NEXT_PUBLIC_FE_URL}/signin`};
  }

  try {
    await sendPasswordResetEmail(getAuth(), email, firebaseOpts);
  } catch (err) {
    const firebaseErr = err as FirebaseError;

    if (opts?.suppressEmailErrors) {
      // Only rethrow error if the error code is not for invalid email or if the
      // email/user doesn't exist.
      // This way, attempts at making this request would not leak information
      // about the validity of email addresses.
      // Note: This only suppresses the error at UI level. The network request
      //       made to Firebase would still show as having error in the browser
      //       network inspector. There isn't all that much we can do about it
      //       without moving this functionality to the API server.
      if (
        firebaseErr?.code !== 'auth/invalid-email' &&
        firebaseErr?.code !== 'auth/user-not-found'
      ) {
        throw err;
      }
    } else {
      throw err;
    }
  }
};

/**
 * The `useFirebase` hook provides various general usage and utility functions
 * specific to Firebase.
 * This hook is not responsible for performing **initialization related tasks**
 * for Firebase integration. Initialization for Firebase is done inside
 * `AuthProvider`.
 */
export const useFirebase = () => {
  return {
    getCurrentUser: getCurrentFirebaseUser,
    signUp: signUpWithFirebase,
    signIn: signInWithFirebase,
    signOut: signOutWithFirebase,
    verifyCredential: verifyCredentialWithFirebase,
    resetPassword: resetPasswordWithFirebase,
  };
};
